import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MobileBottomNav from "../../components/feature/MobileBottomNav";
import useAuthStatus from "../../hooks/useAuthStatus";
import { withdrawCryptoAPI } from "../../api/apiServices";
import { useAssets } from "../../hooks/useAssets";
import { useWallet } from "../../hooks/useWallet";
import { useTradingConfig } from "../../hooks/useTradingConfig";
import ActionGate from "../../components/feature/ActionGate";
import PageLoader from "../../components/PageLoader";
import ServiceGate from "../../components/feature/ServiceGate";
import { useServices } from "../../hooks/useServices";

export default function WithdrawCrypto() {
  const { services } = useServices();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStatus();

  const [step, setStep] = useState("form");
  const [selectedSymbol, setSelectedSymbol] = useState("USDT");
  const [address, setAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [error, setError] = useState(null);

  const { fees, config } = useTradingConfig({ enabled: isAuthenticated });

  const { priceMap, loading: assetsLoading } = useAssets();
  const { holdings, nairaBalance, loading: walletLoading } = useWallet({ enabled: isAuthenticated, priceMap });

  const loading = assetsLoading || walletLoading;

  const formatNetwork = (net) => {
    const maps = {
      bsc: "BNB Smart Chain (BEP20)", eth: "Ethereum (ERC20)",
      ton: "TON Network", solana: "Solana", tron: "TRC20",
    };
    return maps[net] || net?.toUpperCase() || "";
  };

  // holdings from useWallet already has valueNGN, image, color, icon, networks[]
  // We just need to add rawNetwork for the withdraw payload
  const holding = useMemo(() => {
    const h = holdings.find(h => h.symbol === selectedSymbol) || holdings[0];
    if (!h) return null;
    const primaryNetwork = h.networks?.[0]?.network || "";
    return {
      ...h,
      amount:      h.totalBalance,
      network:     formatNetwork(primaryNetwork),
      rawNetwork:  primaryNetwork,
    };
  }, [holdings, selectedSymbol]);

  const fee = 0.0001;
  const priceNgn = priceMap[selectedSymbol]?.priceNgn || 0;
  const nairaEquiv = amount ? (parseFloat(amount) * priceNgn).toFixed(2) : "0";
  const currentNetwork = holding?.network || "Select Asset";

  const handleConfirm = async () => {
    setStep("processing");
    setError(null);
    try {
      const payload = {
        currency:  selectedSymbol,
        network:   holding?.rawNetwork,
        toAddress: address,
        amount:    String(amount),
      };
      const response = await withdrawCryptoAPI(payload);
      if (response.status === true) {
        setStep("success");
      } else {
        setError(response.message || "Withdrawal failed. Please try again.");
        setStep("form");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || "An unexpected error occurred.";
      setError(errorMsg);
      setStep("form");
      alert(errorMsg);
    }
  };


  if (loading) return <PageLoader size="md" message="Crypto assets loading..." />

  return (
    <ServiceGate serviceKey="withdrawals" services={services} fullPage>
      <div className="min-h-screen bg-bg pb-28">
      <div className="bg-surface px-5 pt-12 pb-4 flex items-center gap-3 border-b border-border">
        <button
          onClick={() => step === "form" ? navigate("/home") : setStep("form")}
          className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center cursor-pointer hover:bg-surface-muted"
        >
          <i className="ri-arrow-left-line text-secondary text-lg"></i>
        </button>
        <h1 className="text-primary font-bold text-lg font-display">
          Withdraw Crypto
        </h1>
      </div>

      <div className="px-5 py-4">
        {step === "form" && (
          <div>
            {error && (
              <div className="bg-danger-light border border-danger-border text-danger-text p-3 rounded-xl mb-4 text-xs flex items-center gap-2">
                <i className="ri-error-warning-line"></i>
                {typeof error === "string" ? error : "Transaction failed. Please check your details."}
              </div>
            )}
            <p className="text-secondary text-sm mb-4">Send crypto to any external wallet</p>

            {/* Holdings selector */}
            {loading ? (
              <div className="text-sm text-muted py-6">Loading portfolio...</div>
            ) : !holdings.length ? (
              <div className="text-sm text-muted py-6">No crypto holdings available</div>
            ) : (
              <div className="flex gap-2 overflow-x-auto pb-2 mb-5">
                {holdings.map((h) => (
                  <button
                    key={h.symbol}
                    onClick={() => setSelectedSymbol(h.symbol)}
                    className="flex-shrink-0 flex flex-col items-center gap-1 px-4 py-2 rounded-xl border transition-all cursor-pointer"
                    style={{
                      // Asset colors are per-asset runtime data (not a fixed
                      // semantic tone), so they stay computed rather than
                      // token-referenced — same reasoning as Wallet.jsx.
                      background:   selectedSymbol === h.symbol ? `${h.color}15` : "var(--color-surface)",
                      borderColor:  selectedSymbol === h.symbol ? h.color : "var(--color-border-strong)",
                    }}
                  >
                    <span className="text-xs font-bold" style={{ color: selectedSymbol === h.symbol ? h.color : "var(--color-text-muted)" }}>
                      {h.symbol}
                    </span>
                    <span className="text-muted text-xs">
                      {Number(h.totalBalance).toLocaleString(undefined, { maximumFractionDigits: 6 })}
                    </span>
                  </button>
                ))}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="text-secondary text-xs font-medium mb-2 block">Recipient Wallet Address</label>
                <div className="relative">
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value.trim())}
                    placeholder={`Enter ${selectedSymbol} wallet address`}
                    className="w-full bg-surface-muted border border-border-strong rounded-xl py-4 pl-4 pr-12 text-primary text-sm placeholder:text-faint focus:outline-none focus:border-brand focus:bg-surface transition-colors"
                  />
                  <button className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center cursor-pointer">
                    <i className="ri-qr-scan-line text-muted text-lg"></i>
                  </button>
                </div>
              </div>

              <div>
                <label className="text-secondary text-xs font-medium mb-2 block">Amount ({selectedSymbol})</label>
                <div className="relative">
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00000000"
                    className="w-full bg-surface-muted border border-border-strong rounded-xl py-4 pl-4 pr-16 text-primary text-sm placeholder:text-faint focus:outline-none focus:border-brand focus:bg-surface transition-colors"
                  />
                  <button
                    onClick={() => setAmount(String(holding?.totalBalance || 0))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-text text-xs cursor-pointer whitespace-nowrap font-semibold bg-brand-light px-2 py-1 rounded-lg"
                  >
                    MAX
                  </button>
                </div>
                <p className="text-muted text-xs mt-1">
                  Available: {Number(holding?.totalBalance || 0).toLocaleString()} {selectedSymbol} · ≈ ₦{Number(nairaEquiv || 0).toLocaleString()}
                </p>
                {parseFloat(amount || 0) > parseFloat(holding?.totalBalance || 0) && (
                  <p className="text-danger text-xs mt-1">Insufficient balance</p>
                )}
              </div>
            </div>

            <div className="bg-surface-muted border border-border rounded-xl px-4 py-3 space-y-2 my-5">
              <div className="flex justify-between text-xs">
                <span className="text-muted">Network Fee</span>
                <span className="text-secondary">{fee} {selectedSymbol}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted">Network</span>
                <span className="text-secondary">{currentNetwork}</span>
              </div>
            </div>

            <div className="bg-danger-light border border-danger-border rounded-xl px-4 py-3 mb-6 flex items-start gap-3">
              <i className="ri-alert-line text-danger text-lg mt-0.5"></i>
              <p className="text-danger-text text-xs leading-relaxed">
                Double-check the wallet address. Crypto withdrawals are <strong>irreversible</strong>.
              </p>
            </div>
            <ActionGate action="withdraw_crypto" config={config}>
              <button
              onClick={() => setStep("confirm")}
              disabled={
                !address || !amount || parseFloat(amount) <= 0 ||
                parseFloat(amount) > parseFloat(holding?.totalBalance || 0)
              }
              className="w-full py-4 rounded-2xl text-on-brand font-semibold text-base transition-all active:scale-95 disabled:opacity-40 bg-brand hover:bg-brand-hover"
            >
              Continue
            </button>
            </ActionGate>


            
          </div>
        )}

        {step === "confirm" && (
          <div>
            <h2 className="text-primary text-xl font-bold mb-1 font-display">Confirm Withdrawal</h2>
            <p className="text-muted text-sm mb-6">This action is irreversible</p>
            <div className="bg-surface border border-border shadow-card rounded-2xl p-5 space-y-4 mb-6">
              <div className="flex justify-between"><span className="text-muted text-sm">Asset</span><span className="text-primary font-bold">{selectedSymbol}</span></div>
              <div className="flex justify-between"><span className="text-muted text-sm">Amount</span><span className="text-primary font-bold">{amount} {selectedSymbol}</span></div>
              <div className="flex justify-between"><span className="text-muted text-sm">Network Fee</span><span className="text-secondary">{fee} {selectedSymbol}</span></div>
              <div className="flex justify-between"><span className="text-muted text-sm">Network</span><span className="text-secondary">{currentNetwork}</span></div>
              <div className="border-t border-border pt-3">
                <span className="text-muted text-xs block mb-1">To Address</span>
                <span className="text-secondary text-xs break-all font-mono">{address}</span>
              </div>
            </div>
            <button onClick={handleConfirm} className="w-full py-4 rounded-2xl text-on-brand font-semibold bg-brand hover:bg-brand-hover">
              Confirm Withdrawal
            </button>
          </div>
        )}

        {step === "processing" && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <div className="w-20 h-20 rounded-full bg-brand-light flex items-center justify-center mb-6" style={{ boxShadow: "inset 0 0 0 1px var(--color-brand-ring)" }}>
              <i className="ri-loader-4-line text-brand text-4xl animate-spin"></i>
            </div>
            <h2 className="text-primary text-xl font-bold mb-2 font-display">Broadcasting Transaction</h2>
            <p className="text-muted text-sm">Sending {amount} {selectedSymbol}...</p>
          </div>
        )}

        {step === "success" && (
          <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
            <div className="w-20 h-20 rounded-full bg-brand-light border-2 border-brand-border flex items-center justify-center mb-6">
              <i className="ri-check-line text-brand text-4xl"></i>
            </div>
            <h2 className="text-primary text-2xl font-bold mb-2 font-display">Withdrawal Sent!</h2>
            <p className="text-muted text-sm mb-6">Broadcasted to {currentNetwork}</p>
            <div className="bg-brand-light border border-brand-border rounded-2xl px-6 py-4 mb-8 w-full max-w-xs">
              <p className="text-muted text-xs mb-1">Amount Sent</p>
              <p className="text-brand-text text-3xl font-bold font-display">{amount} {selectedSymbol}</p>
            </div>
            <button onClick={() => navigate("/home")} className="w-full max-w-xs py-4 rounded-2xl text-on-brand font-semibold bg-brand hover:bg-brand-hover">
              Back to Home
            </button>
          </div>
        )}
      </div>
      <MobileBottomNav />
    </div>
    </ServiceGate>
    
  );
}
